/**
 * Central place for the API base URL and every endpoint path used by the app.
 * Nothing else in the codebase should hardcode a URL string -- import from here.
 *
 * BASE_URL is read from the CRA env var REACT_APP_API_BASE_URL (set it in
 * .env / .env.local / .env.production -- see .env.example), falling back to
 * localhost for local dev against the backend from SETUP.md.
 */
//export const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
export const BASE_URL = "https://api.hirality.com";


const API = "/api/v1/ats";

export const ENDPOINTS = {
  // ---------- Auth ----------
  login: `/auth/login`, // standalone dev-only login; swap for Hirality's real employee login on integration
  clientLogin: (companyId) => `${API}/auth/client-login?company_id=${companyId}`,

  // ---------- Identity & Roles ----------
  me: `${API}/me`,
  permissions: `${API}/permissions`,
  roles: `${API}/roles`,
  role: (id) => `${API}/roles/${id}`,
  userRoles: `${API}/user-roles`,
  userRole: (userType, identityId) => `${API}/user-roles/${userType}/${identityId}`,
  customers: `${API}/customers`,
  customerContacts: (customerId) => `${API}/customers/${customerId}/contacts`,
  teams: `${API}/teams`,
  team: (id) => `${API}/teams/${id}`,
  teamMembers: (id) => `${API}/teams/${id}/members`,
  teamMember: (id, employeeId) => `${API}/teams/${id}/members/${employeeId}`,
  employees: `/employees/`, // HRMS's own listing endpoint -- read-only from ATS, still used to build the directory
  employeeCreate: `${API}/employees`, // ATS-owned creation endpoint -- separate table/flow, see identityService.createEmployee
  employeeProfiles: `${API}/employees/profiles`,
  employeeProfile: (id) => `${API}/employees/${id}/profile`,

  // ---------- Tickets ----------
  tickets: `${API}/tickets`,
  ticketsBoard: `${API}/tickets/board`,
  ticket: (id) => `${API}/tickets/${id}`,
  ticketBulkUpdate: `${API}/tickets/bulk-update`,
  ticketHistory: (id) => `${API}/tickets/${id}/history`,
  ticketSla: (id) => `${API}/tickets/${id}/sla`,
  ticketComments: (id) => `${API}/tickets/${id}/comments`,
  ticketAttachments: (id) => `${API}/tickets/${id}/attachments`,
  ticketAttachmentUpload: (id) => `${API}/tickets/${id}/attachments/upload`,
  ticketAttachmentDownload: (id, attachmentId) => `${API}/tickets/${id}/attachments/${attachmentId}/download`,
  ticketWatchers: (id) => `${API}/tickets/${id}/watchers`,
  ticketWatcher: (id, employeeId) => `${API}/tickets/${id}/watchers/${employeeId}`,
  dashboardSummary: `${API}/dashboard/summary`,

  // ---------- SLA & Notifications ----------
  slaPolicies: `${API}/sla/policies`,
  slaPolicy: (id) => `${API}/sla/policies/${id}`,
  statuses: `${API}/statuses`,
  priorities: `${API}/priorities`,
  slaBusinessHours: `${API}/sla/business-hours`,
  slaHolidays: `${API}/sla/holidays`,
  slaHoliday: (id) => `${API}/sla/holidays/${id}`,
  slaRunEscalationCheck: `${API}/sla/run-escalation-check`,
  notifications: `${API}/notifications`,
  notificationRead: (id) => `${API}/notifications/${id}/read`,
  notificationsMarkAllRead: `${API}/notifications/mark-all-read`,

  // ---------- Statuses & Workflow ----------
  status: (id) => `${API}/statuses/${id}`,
  workflowTransitions: `${API}/workflow/transitions`,

  // ---------- Plans (sprints) ----------
  plans: `${API}/plans`,
  plan: (id) => `${API}/plans/${id}`,
  planStart: (id) => `${API}/plans/${id}/start`,
  planComplete: (id) => `${API}/plans/${id}/complete`,
  planTickets: (id) => `${API}/plans/${id}/tickets`,
  planBacklogTickets: `${API}/plans/backlog/tickets`,

  // ---------- Projects & Reports ----------
  projects: `${API}/projects`,
  project: (id) => `${API}/projects/${id}`,
  projectMembers: (id) => `${API}/projects/${id}/members`,
  projectMember: (id, employeeId) => `${API}/projects/${id}/members/${employeeId}`,
  report: (name) => `${API}/reports/${name}`,
  reportExport: (name) => `${API}/reports/${name}/export`,

  // ---------- Knowledge Base ----------
  kbCategories: `${API}/kb/categories`,
  kbCategory: (id) => `${API}/kb/categories/${id}`,
  kbArticles: `${API}/kb/articles`,
  kbArticle: (id) => `${API}/kb/articles/${id}`,
  kbArticleSubmitForReview: (id) => `${API}/kb/articles/${id}/submit-for-review`,
  kbArticlePublish: (id) => `${API}/kb/articles/${id}/publish`,
  kbArticleVersions: (id) => `${API}/kb/articles/${id}/versions`,

  // ---------- Assets ----------
  assets: `${API}/assets`,
  asset: (id) => `${API}/assets/${id}`,
  assetExpiringWarranties: `${API}/assets/expiring-warranties`,
  assetAssign: (id) => `${API}/assets/${id}/assign`,
  assetUnassign: (id) => `${API}/assets/${id}/unassign`,
  assetLinkTicket: (id) => `${API}/assets/${id}/link-ticket`,

  // ---------- Automation & Integrations ----------
  automationRules: `${API}/automation/rules`,
  automationRule: (id) => `${API}/automation/rules/${id}`,
  webhooks: `${API}/webhooks`,
  webhook: (id) => `${API}/webhooks/${id}`,
  webhookDeliveries: (id) => `${API}/webhooks/${id}/deliveries`,
  integrations: `${API}/integrations`,

  // ---------- Search ----------
  search: `${API}/search`,

  // ---------- Health ----------
  health: `/health`,
};
